import React, { useState } from 'react';
import { formatCurrency } from '../utils/formatCurrency';

const EXPENSE_TYPES = ['FOOD', 'GO OUT', 'BILLS', 'AMANDA', 'MARTIN', 'HOLIDAYS', 'HOUSE', 'OTHERS'];
const MONEY_SOURCES = ['MARTIN BANK', 'AMANDA BANK', 'PAGO EM DINHEIRO'];
const CURRENCIES = ['POUNDS', 'EUR', 'BRL'];

const TransactionForm = ({ onAddTransaction, conversionRates, defaultMinimized = false }) => {
  const [isExpanded, setIsExpanded] = useState(!defaultMinimized); // Usa la prop defaultMinimized
  const [formData, setFormData] = useState({
    merchantName: '',
    currency: 'POUNDS',
    amount: '',
    expenseType: 'FOOD',
    moneySource: 'MARTIN BANK',
    bankName: '',
    date: new Date().toISOString().split('T')[0], // Fecha actual como default
    hasSplit: false,
    splits: [
      { expenseType: 'FOOD', moneySource: 'MARTIN BANK', amount: '' },
      { expenseType: 'FOOD', moneySource: 'MARTIN BANK', amount: '' },
      { expenseType: 'FOOD', moneySource: 'MARTIN BANK', amount: '' }
    ]
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSplitToggle = () => {
    setFormData(prev => ({
      ...prev,
      hasSplit: !prev.hasSplit,
      splits: prev.splits.map(split => ({ ...split, amount: '' }))
    }));
  };

  const handleSplitChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      splits: prev.splits.map((split, i) => 
        i === index ? { ...split, [field]: value } : split
      )
    }));
  };

  const convertToGBP = (amount, currency) => {
    const num = parseFloat(amount) || 0;
    if (currency === 'POUNDS') return num;
    if (currency === 'EUR') return num / (conversionRates.EUR || 1.165);
    if (currency === 'BRL') return num / (conversionRates.BRL || 6.36);
    return num;
  };

  const calculateSplitTotal = () => {
    return formData.splits.reduce((total, split) => {
      return total + (parseFloat(split.amount) || 0);
    }, 0);
  };

  const getRemainingAmount = () => {
    const originalAmount = parseFloat(formData.amount) || 0;
    const splitTotal = calculateSplitTotal();
    return originalAmount - splitTotal;
  };

  const fillRemainingAmount = (splitIndex) => {
    const remainingAmount = getRemainingAmount();
    if (remainingAmount > 0) {
      handleSplitChange(splitIndex, 'amount', remainingAmount.toFixed(2));
    }
  };

  const isSplitValid = () => {
    if (!formData.hasSplit) return true;
    const splitTotal = calculateSplitTotal();
    const originalAmount = parseFloat(formData.amount) || 0;
    return Math.abs(splitTotal - originalAmount) < 0.01;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.merchantName || !formData.amount) {
      alert('Por favor, preencha o nome do estabelecimento e o valor');
      return;
    }

    if (formData.hasSplit && !isSplitValid()) {
      alert('A soma das divisões deve ser igual ao valor original');
      return;
    }

    const baseTransaction = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      merchantName: formData.merchantName,
      currency: formData.currency,
      originalAmount: parseFloat(formData.amount),
      gbpAmount: convertToGBP(formData.amount, formData.currency),
      date: formData.date,
      timestamp: Date.now()
    };

    if (formData.hasSplit) {
      // Crear múltiples transacciones para cada split activo
      const activeSplits = formData.splits.filter(split => split.amount && parseFloat(split.amount) > 0);
      
      activeSplits.forEach((split, index) => {
        const splitTransaction = {
          ...baseTransaction,
          id: baseTransaction.id + index,
          originalAmount: parseFloat(split.amount),
          gbpAmount: convertToGBP(split.amount, formData.currency),
          expenseType: split.expenseType,
          moneySource: split.moneySource,
          bankName: formData.bankName,
          isSplit: true,
          splitInfo: {
            totalSplits: activeSplits.length,
            splitIndex: index
          }
        };
        onAddTransaction(splitTransaction);
      });
    } else {
      const transaction = {
        ...baseTransaction,
        expenseType: formData.expenseType,
        moneySource: formData.moneySource,
        bankName: formData.bankName,
        isSplit: false
      };
      onAddTransaction(transaction);
    }

    // Reset form
    setFormData({
      merchantName: '',
      currency: 'POUNDS',
      amount: '',
      expenseType: 'FOOD',
      moneySource: 'MARTIN BANK',
      bankName: '',
      date: new Date().toISOString().split('T')[0], // Fecha actual como default
      hasSplit: false,
      splits: [
        { expenseType: 'FOOD', moneySource: 'MARTIN BANK', amount: '' },
        { expenseType: 'FOOD', moneySource: 'MARTIN BANK', amount: '' },
        { expenseType: 'FOOD', moneySource: 'MARTIN BANK', amount: '' }
      ]
    });
  };

  return (
    <div className="expense-card">
      <div className="flex items-center justify-between cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
        <div>
          <h2 className="text-2xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
            Adicionar Nova Transação
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Registro manual de gastos
          </p>
        </div>
        <button 
          type="button"
          className="button-secondary text-lg"
        >
          {isExpanded ? '▼' : '▶'}
        </button>
      </div>
      
      {isExpanded && (
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
        {/* Merchant Name and Date */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome do Estabelecimento
            </label>
            <input
              type="text"
              value={formData.merchantName}
              onChange={(e) => handleInputChange('merchantName', e.target.value)}
              className="input-field"
              placeholder="ex. Amazon, Carrefour, McDonald's"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data da Transação
            </label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => handleInputChange('date', e.target.value)}
              className="input-field"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Banco (opcional)
            </label>
            <input
              type="text"
              value={formData.bankName}
              onChange={(e) => handleInputChange('bankName', e.target.value)}
              className="input-field"
              placeholder="ex. Monzo, Revolut, Nubank"
            />
          </div>
        </div>

        {/* Currency and Amount */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Moeda
            </label>
            <select
              value={formData.currency}
              onChange={(e) => handleInputChange('currency', e.target.value)}
              className="select-field"
            >
              {CURRENCIES.map(currency => (
                <option key={currency} value={currency}>{currency}</option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Valor Original
            </label>
            <input
              type="number"
              step="0.01"
              value={formData.amount}
              onChange={(e) => handleInputChange('amount', e.target.value)}
              className="input-field"
              placeholder="0.00"
              required
            />
            {formData.amount && formData.currency !== 'POUNDS' && (
              <div className="text-xs text-gray-500 mt-1">
                ≈ {formatCurrency(convertToGBP(formData.amount, formData.currency))}
              </div>
            )}
          </div>
        </div>

        {/* Split Toggle */}
        <div className="flex items-center space-x-3">
          <input
            type="checkbox"
            id="hasSplit"
            checked={formData.hasSplit}
            onChange={handleSplitToggle}
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <label htmlFor="hasSplit" className="text-sm font-medium text-gray-700">
            Ativar Divisão (dividir em múltiplas categorias)
          </label>
        </div>

        {/* Main fields or Split fields */}
        {!formData.hasSplit ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo de Gasto
              </label>
              <select
                value={formData.expenseType}
                onChange={(e) => handleInputChange('expenseType', e.target.value)}
                className="select-field"
              >
                {EXPENSE_TYPES.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fonte do Dinheiro
              </label>
              <select
                value={formData.moneySource}
                onChange={(e) => handleInputChange('moneySource', e.target.value)}
                className="select-field"
              >
                {MONEY_SOURCES.map(source => (
                  <option key={source} value={source}>{source}</option>
                ))}
              </select>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-800">Configurar Divisões</h3>
            
            {formData.splits.map((split, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Divisão {index + 1}</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Tipo</label>
                    <select
                      value={split.expenseType}
                      onChange={(e) => handleSplitChange(index, 'expenseType', e.target.value)}
                      className="select-field text-sm"
                    >
                      {EXPENSE_TYPES.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Fonte</label>
                    <select
                      value={split.moneySource}
                      onChange={(e) => handleSplitChange(index, 'moneySource', e.target.value)}
                      className="select-field text-sm"
                    >
                      {MONEY_SOURCES.map(source => (
                        <option key={source} value={source}>{source}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Valor</label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        step="0.01"
                        value={split.amount}
                        onChange={(e) => handleSplitChange(index, 'amount', e.target.value)}
                        className="input-field text-sm flex-1"
                        placeholder="0.00"
                      />
                      <button
                        type="button"
                        onClick={() => fillRemainingAmount(index)}
                        className="px-2 py-1 text-xs bg-blue-100 text-blue-600 rounded hover:bg-blue-200 transition-colors"
                        title={`Completar com o valor restante: ${getRemainingAmount().toFixed(2)}`}
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            
            {/* Split validation */}
            <div className="text-sm">
              <div className="flex justify-between items-center">
                <span>Total da divisão:</span>
                <span className={calculateSplitTotal() === parseFloat(formData.amount || 0) ? 'text-green-600' : 'text-red-600'}>
                  {calculateSplitTotal().toFixed(2)} {formData.currency}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span>Valor original:</span>
                <span>{parseFloat(formData.amount || 0).toFixed(2)} {formData.currency}</span>
              </div>
              {!isSplitValid() && (
                <div className="text-red-600 text-xs mt-1">
                  As divisões devem somar o valor original
                </div>
              )}
            </div>
          </div>
        )}

        <button
          type="submit"
          className={`w-full text-lg py-4 font-semibold rounded-xl shadow-lg transition-all duration-300 ${
            formData.hasSplit && !isSplitValid() 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'button-success hover:shadow-xl hover:scale-105'
          }`}
          disabled={formData.hasSplit && !isSplitValid()}
        >
          Adicionar Transação
        </button>
        </form>
      )}
    </div>
  );
};

export default TransactionForm; 